/**
 * Special characters regex - matches characters that are not allowed in names
 * Based on old version: /[`!@#$%^&*[()_+={}\];':"\\|,.<>?~¡¨°Ñ-]/
 */
const SPECIAL_CHARS_REGEX = /[`!@#$%^&*[()_+={}\];':"\\|,.<>?~¡¨°Ñ-]/;

/**
 * Price format regex - validates price values
 * Allows: numbers with optional thousands separator and up to 2 decimal places
 */
const PRICE_FORMAT_REGEX = /^\d*(((,\d{3}){1})?(\.\d{0,2})?)$/;

/**
 * Sorts rate card windows by windowOrder for consistent column display.
 * @param {Array} windows
 * @returns {Array}
 */
export function sortRateCardWindows(windows = []) {
  return [...windows].sort(
    (a, b) => (a.window?.windowOrder ?? 0) - (b.window?.windowOrder ?? 0),
  );
}

/**
 * Resolves tapWindowId for a price column index using sorted window order.
 * @param {Array} windows
 * @param {number} colIdx
 * @returns {string|number|undefined}
 */
export function getTapWindowIdForPriceColumn(windows, colIdx) {
  const sorted = sortRateCardWindows(windows);
  const windowEntry = sorted[colIdx];
  return windowEntry?.id?.tapWindowId ?? windowEntry?.id;
}

/**
 * Validates a string for special characters
 * @param {string} value - The value to validate
 * @returns {string|undefined} - Error message if invalid, undefined if valid
 */
export function validateSpecialCharacters(value) {
  if (!value) return undefined;
  if (SPECIAL_CHARS_REGEX.test(value)) {
    return "Input may not contain [`!@#$%^&*[()_+={}];':\"\\|,.<>?~¡¨°Ñ-]";
  }
  if (value.toUpperCase().includes("COPY")) {
    return "Input may not contain COPY";
  }
  return undefined;
}

/**
 * Validates a price value format
 * @param {string|number} value - The price value to validate
 * @returns {string|undefined} - Error message if invalid, undefined if valid
 */
export function validatePriceFormat(value) {
  if (value === null || value === undefined || value === "") {
    return undefined; // Required validation is handled separately
  }
  const strValue = String(value);
  if (!PRICE_FORMAT_REGEX.test(strValue)) {
    return "Invalid price format";
  }
  return undefined;
}

/**
 * Trims price input to at most two decimal places (optional thousands commas).
 * @param {string} raw - Raw input
 * @returns {string}
 */
export function normalizePriceInput(raw) {
  if (raw === null || raw === undefined) return "";
  const s = String(raw).replace(/[^\d.,]/g, "");
  const firstDot = s.indexOf(".");
  if (firstDot === -1) return s;
  const intPart = s.slice(0, firstDot + 1);
  const afterDot = s
    .slice(firstDot + 1)
    .replace(/\D/g, "")
    .slice(0, 2);
  return intPart + afterDot;
}

/**
 * Maps API tier prices (e.g. 4 decimal places) to 2-decimal strings for the edit form
 * so validation and display stay consistent.
 * @param {Object} rateCard
 * @returns {Object}
 */
export function normalizeRateCardTierPricesForForm(rateCard) {
  if (!rateCard || !Array.isArray(rateCard.tierAliases)) return rateCard;
  const tierAliases = rateCard.tierAliases.map((alias) => ({
    ...alias,
    tiers: Array.isArray(alias.tiers)
      ? alias.tiers.map((tier) => {
          const p = tier.price;
          if (p === null || p === undefined || p === "") {
            return { ...tier };
          }
          const n = parseFloat(String(p).replace(/,/g, ""));
          if (Number.isNaN(n)) {
            return { ...tier };
          }
          return { ...tier, price: n.toFixed(2) };
        })
      : alias.tiers,
  }));
  return { ...rateCard, tierAliases };
}

/**
 * @param {Object} alias - Tier alias row
 * @param {boolean} isCreate - Create vs edit
 * @returns {boolean}
 */
function hasTierAliasFormatValue(alias, isCreate) {
  if (isCreate) {
    return (
      Array.isArray(alias.tierAliasFormat) && alias.tierAliasFormat.length > 0
    );
  }
  if (typeof alias.tierAliasFormat === "string") {
    return Boolean(alias.tierAliasFormat.trim());
  }
  if (Array.isArray(alias.tierAliasFormat)) {
    return alias.tierAliasFormat.length > 0;
  }
  return false;
}

/**
 * Validates the rate card form data
 * @param {Object} formData - The form data to validate
 * @param {"create"|"edit"} mode - The mode of the form
 * @param {boolean} percentagesEnabled - Whether percentage fields are enabled
 * @returns {Object} - { isValid: boolean, errors: Object }
 */
export function validateRateCardForm(formData, mode, percentagesEnabled) {
  const isCreate = mode === "create";
  const errors = {};

  if (!formData?.rateCardName?.trim()) {
    errors.rateCardName = "Name is required";
  } else {
    const nameError = validateSpecialCharacters(formData.rateCardName);
    if (nameError) {
      errors.rateCardName = nameError;
    }
  }

  if (isCreate) {
    if (!formData?.windowSequence?.value) {
      errors.windowSequence = "Window Sequence is required";
    }
    if (!formData?.currency?.value) {
      errors.currency = "Currency is required";
    }
    if (!formData?.priceType?.value) {
      errors.priceType = "Price Type is required";
    }
    if (!formData?.revShareType?.value) {
      errors.revShareType = "Rev Share Type is required";
    }
  } else {
    if (!formData?.windowSequenceId) {
      errors.windowSequence = "Window Sequence is required";
    }
    if (!formData?.currency) {
      errors.currency = "Currency is required";
    }
    if (!formData?.priceType) {
      errors.priceType = "Price Type is required";
    }
    if (!formData?.revShareType) {
      errors.revShareType = "Rev Share Type is required";
    }
  }

  if (percentagesEnabled && Array.isArray(formData?.windows)) {
    formData.windows.forEach((w, idx) => {
      if (!w.percentage && w.percentage !== 0) {
        errors[`percentage_${w.id?.tapWindowId || idx}`] =
          "Percentage is required";
      }
    });
  }

  const tierAliases = formData?.tierAliases || [];
  const tierAliasNames = {};

  tierAliases.forEach((alias, idx) => {
    if (!alias.tierAliasName?.trim()) {
      errors[`tierAliasName_${idx}`] = "Tier Name is required";
    } else {
      const aliasNameError = validateSpecialCharacters(alias.tierAliasName);
      if (aliasNameError) {
        errors[`tierAliasName_${idx}`] = aliasNameError;
      }

      const normalizedName = alias.tierAliasName.trim().toLowerCase();
      if (tierAliasNames[normalizedName]) {
        errors[`tierAliasName_${idx}`] = "Duplicate Tier Name";
        const firstIdx = tierAliasNames[normalizedName];
        if (!errors[`tierAliasName_${firstIdx}`]) {
          errors[`tierAliasName_${firstIdx}`] = "Duplicate Tier Name";
        }
      } else {
        tierAliasNames[normalizedName] = idx;
      }
    }

    if (!hasTierAliasFormatValue(alias, isCreate)) {
      errors[`tierAliasFormat_${idx}`] = "Format is required";
    }
  });

  if (isCreate && Array.isArray(formData?.tierRows)) {
    const allTierNames = {};

    formData.tierRows.forEach((row, rowIdx) => {
      if (Array.isArray(row.aliasColumns)) {
        row.aliasColumns.forEach((col, colIdx) => {
          const tierName = col.tierName;
          if (!tierName && tierName !== 0) {
            errors[`tierRow_${rowIdx}_alias_${colIdx}`] =
              "Tier Name is required";
          } else {
            const tierNameError = validateSpecialCharacters(String(tierName));
            if (tierNameError) {
              errors[`tierRow_${rowIdx}_alias_${colIdx}`] = tierNameError;
            }

            if (colIdx === 0) {
              const normalizedTierName = String(tierName).trim().toLowerCase();
              if (allTierNames[normalizedTierName]) {
                errors[`tierRow_${rowIdx}_alias_${colIdx}`] =
                  "Duplicate Tier Name";
              } else {
                allTierNames[normalizedTierName] = { rowIdx, colIdx };
              }
            }
          }
        });
      }
      if (Array.isArray(row.priceColumns)) {
        row.priceColumns.forEach((col, colIdx) => {
          if (!col.price && col.price !== 0) {
            errors[`tierRow_${rowIdx}_price_${colIdx}`] = "Price is required";
          } else {
            const priceError = validatePriceFormat(col.price);
            if (priceError) {
              errors[`tierRow_${rowIdx}_price_${colIdx}`] = priceError;
            }
          }
        });
      }
    });
  }

  if (!isCreate && Array.isArray(tierAliases)) {
    const allTierNames = {};
    const firstWindowId = formData?.windows?.[0]?.id?.tapWindowId;

    tierAliases.forEach((alias, aliasIdx) => {
      if (Array.isArray(alias.tiers)) {
        alias.tiers.forEach((tier, tierIdx) => {
          if (!tier.tierName && tier.tierName !== 0) {
            errors[`tier_${aliasIdx}_${tierIdx}_name`] =
              "Tier Name is required";
          } else {
            const tierNameError = validateSpecialCharacters(
              String(tier.tierName),
            );
            if (tierNameError) {
              errors[`tier_${aliasIdx}_${tierIdx}_name`] = tierNameError;
            }

            if (tier.id?.tapWindowId === firstWindowId) {
              const normalizedTierName = String(tier.tierName)
                .trim()
                .toLowerCase();
              if (allTierNames[normalizedTierName]) {
                errors[`tier_${aliasIdx}_${tierIdx}_name`] =
                  "Duplicate Tier Name";
              } else {
                allTierNames[normalizedTierName] = { aliasIdx, tierIdx };
              }
            }
          }

          if (!tier.price && tier.price !== 0) {
            errors[`tier_${aliasIdx}_${tierIdx}_price`] = "Price is required";
          } else {
            const priceError = validatePriceFormat(tier.price);
            if (priceError) {
              errors[`tier_${aliasIdx}_${tierIdx}_price`] = priceError;
            }
          }
        });
      }
    });
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

/**
 * Rev share types that disable percentage fields
 */
export const PERCENTAGE_DISABLED_REV_SHARE_TYPES = [
  "REV_SHARE",
  "NON_REV_SHARE",
  "VOD_PPV",
  "HOTEL",
  "REV_SHARE_PLUS",
  "REV SHARE",
  "NON-REV SHARE",
  "VOD PPV",
  "REV SHARE +",
];

/**
 * Check if percentages are enabled based on rev share type
 * @param {Object} formData - The form data
 * @param {boolean} isCreate - Whether in create mode
 * @returns {boolean} - Whether percentages are enabled
 */
export function getPercentagesEnabled(formData, isCreate) {
  const revShareValue = isCreate
    ? formData?.revShareType?.value
    : formData?.revShareType?.toUpperCase();

  return (
    revShareValue &&
    !PERCENTAGE_DISABLED_REV_SHARE_TYPES.includes(revShareValue)
  );
}
